import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult
} from '@tanstack/react-query'
import { useNetworkState } from '@uidotdev/usehooks'
import { useSnackbar } from 'notistack'
import {
  mutationQueue,
  type OfflineMutation,
  OfflineMutationQueue
} from '../services/offline/mutationQueue'
import apiInstance from '@services/api/apiInstance'
import {
  formDataHasFile,
  getFormDataFilesSize,
  uploadProgressStore
} from '@services/uploadProgress/uploadProgress.store'

interface OfflineMutationOptions<TData = unknown, TError = unknown, TVariables = void>
  extends Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'> {
  endpoint: string | ((variables: TVariables) => string)
  method: 'POST' | 'PATCH' | 'DELETE'
  mutationType: OfflineMutation['mutationType']
  protocolId?: number | ((variables: TVariables) => number)
  maxRetries?: number
  offlineMessage?: string
  transformToFormData?: (variables: TVariables) => FormData
}

export function useOfflineMutation<TData = unknown, TError = unknown, TVariables = void>(
  options: OfflineMutationOptions<TData, TError, TVariables>
): UseMutationResult<TData, TError, TVariables> {
  const network = useNetworkState()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const {
    endpoint,
    method,
    mutationType,
    protocolId,
    maxRetries = 3,
    offlineMessage,
    transformToFormData,
    onSuccess,
    onError,
    onSettled,
    ...mutationOptions
  } = options

  return useMutation<TData, TError, TVariables>({
    ...mutationOptions,
    mutationFn: async (variables: TVariables) => {
      const resolvedEndpoint = typeof endpoint === 'function' ? endpoint(variables) : endpoint
      const resolvedProtocolId =
        typeof protocolId === 'function' ? protocolId(variables) : protocolId

      // If offline, queue the mutation immediately without trying online
      if (!network.online) {
        return await queueOfflineMutation(variables, resolvedEndpoint, resolvedProtocolId)
      }

      // If online, try to execute immediately
      let tracksUploadProgress = false
      try {
        // Transform to FormData if needed
        const requestData = transformToFormData ? transformToFormData(variables) : variables

        const headers = {
          ...(!(requestData instanceof FormData) && { 'Content-Type': 'application/json' })
        }

        tracksUploadProgress = requestData instanceof FormData && formDataHasFile(requestData)

        const estimatedTotal =
          tracksUploadProgress && requestData instanceof FormData
            ? getFormDataFilesSize(requestData)
            : 0

        if (tracksUploadProgress && requestData instanceof FormData) {
          const current = uploadProgressStore.getState()
          // Keep batch meta (fileIndex/totalFiles) set by the caller; refresh size/name.
          if (!current?.totalFiles) {
            uploadProgressStore.startFromFormData(requestData)
          } else if (current.total <= 0 && estimatedTotal > 0) {
            uploadProgressStore.setMeta({
              fileName: current.fileName,
              fileIndex: current.fileIndex,
              totalFiles: current.totalFiles,
              estimatedTotal
            })
          }
        }

        const response = await apiInstance<TData>({
          url: resolvedEndpoint,
          method,
          headers,
          // Don't send data for DELETE requests
          ...(method !== 'DELETE' && { data: requestData }),
          ...(requestData instanceof FormData && { transformRequest: (data) => data }),
          ...(tracksUploadProgress && {
            onUploadProgress: (event) =>
              uploadProgressStore.handleAxiosProgress(event, estimatedTotal)
          })
        })

        if (tracksUploadProgress) {
          uploadProgressStore.setProcessing()
        }

        return response.data
      } catch (error: any) {
        if (tracksUploadProgress) {
          uploadProgressStore.clear()
        }

        // Check if it's a network error - if so, queue for retry
        const isNetworkError =
          !error.response || error.code === 'ERR_NETWORK' || error.code === 'NETWORK_ERROR'

        if (isNetworkError) {
          console.log('Network error detected, queuing mutation for offline processing')
          return await queueOfflineMutation(variables, resolvedEndpoint, resolvedProtocolId, error)
        }

        // If it's a server error (4xx, 5xx), still throw to show user
        throw error
      }
    },
    onSuccess: async (data, variables, context) => {
      // Invalidate cache for online operations
      const resolvedProtocolId =
        typeof protocolId === 'function' ? protocolId(variables) : protocolId
      await invalidateCacheForMutation(
        {
          mutationType,
          protocolId: resolvedProtocolId,
          id: '', // not needed for cache invalidation
          endpoint: '',
          method,
          data: variables,
          headers: {},
          timestamp: Date.now(),
          retryCount: 0,
          maxRetries,
          status: 'completed'
        },
        queryClient
      )

      if (onSuccess) {
        onSuccess(data, variables, context)
      }
    },
    onError: (error, variables, context) => {
      if (onError) {
        onError(error, variables, context)
      }
    },
    onSettled: (data, error, variables, context) => {
      if (uploadProgressStore.getState()) {
        uploadProgressStore.completeCurrentFile()
      }
      if (onSettled) {
        onSettled(data, error, variables, context)
      }
    }
  })

  async function queueOfflineMutation(
    variables: TVariables,
    resolvedEndpoint: string,
    resolvedProtocolId?: number,
    _originalError?: any
  ): Promise<TData> {
    // Transform to FormData if needed
    const requestData = transformToFormData ? transformToFormData(variables) : variables

    const headers = {
      ...(!(requestData instanceof FormData) && { 'Content-Type': 'application/json' })
    }

    let serializedData = requestData as any

    // Handle FormData serialization for offline storage
    if (requestData instanceof FormData) {
      serializedData = await OfflineMutationQueue.serializeFormData(requestData)
    }

    const mutationId = await mutationQueue.queueMutation({
      endpoint: resolvedEndpoint,
      method,
      data: serializedData,
      headers,
      mutationType,
      protocolId: resolvedProtocolId,
      maxRetries
    })

    // Show appropriate user feedback only if offline or explicit error
    if (!network.online || _originalError) {
      const message =
        offlineMessage ||
        (network.online
          ? `Operacja ${mutationType} zostanie ponowiona automatycznie`
          : `Operacja ${mutationType} zostanie wykonana po powrocie połączenia`)

      enqueueSnackbar(message, {
        variant: network.online ? 'warning' : 'info',
        autoHideDuration: 4000
      })
    }

    // Return a placeholder response that indicates queuing
    return { queued: true, mutationId } as unknown as TData
  }
}

// Hook for processing the offline queue
export function useOfflineQueueProcessor() {
  const network = useNetworkState()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const processQueue = async (): Promise<void> => {
    if (!network.online) return

    const pendingMutations = await mutationQueue.getPendingMutations()

    if (pendingMutations.length === 0) return

    console.log(`Processing ${pendingMutations.length} pending mutations`)

    for (const mutation of pendingMutations) {
      try {
        await mutationQueue.updateMutationStatus(mutation.id, 'processing')

        let requestData = mutation.data

        // Deserialize FormData if needed
        if (
          mutation.data &&
          typeof mutation.data === 'object' &&
          mutation.data.type === 'formData'
        ) {
          requestData = OfflineMutationQueue.deserializeFormData(mutation.data)
        }

        // Special handling for complete sign operations
        if (
          mutation.endpoint.includes('/sign-complete/') &&
          requestData instanceof FormData &&
          requestData.get('complete_sign_operation')
        ) {
          // Remove the flag
          requestData.delete('complete_sign_operation')

          // Extract protocolId from endpoint
          const protocolIdMatch = mutation.endpoint.match(/\/protocols\/(\d+)\//)
          const protocolId = protocolIdMatch ? protocolIdMatch[1] : null

          if (protocolId) {
            // Step 1: PATCH with signatures and data
            await apiInstance({
              url: `/admin/protocol/protocols/${protocolId}/`,
              method: 'PATCH',
              headers: mutation.headers,
              data: requestData,
              transformRequest: (data) => data
            })

            // Step 2: POST to finalize signing
            await apiInstance({
              url: `/admin/protocol/protocols/${protocolId}/sign/`,
              method: 'POST',
              headers: {
                authorization: mutation.headers.authorization
              }
            })
          }
        } else {
          // Regular mutation processing
          await apiInstance({
            url: mutation.endpoint,
            method: mutation.method,
            headers: mutation.headers,
            // Don't send data for DELETE requests
            ...(mutation.method !== 'DELETE' && { data: requestData }),
            ...(requestData instanceof FormData && { transformRequest: (data) => data })
          })
        }

        await mutationQueue.updateMutationStatus(mutation.id, 'completed')

        // Invalidate relevant query cache based on mutation type
        await invalidateCacheForMutation(mutation, queryClient)

        console.log(`Mutation ${mutation.id} completed successfully`)
      } catch (error: any) {
        console.error(`Mutation ${mutation.id} failed:`, error)

        const shouldRetry = mutation.retryCount < mutation.maxRetries

        if (shouldRetry) {
          await mutationQueue.updateMutationStatus(
            mutation.id,
            'pending',
            error.message || 'Network error'
          )
        } else {
          await mutationQueue.updateMutationStatus(
            mutation.id,
            'failed',
            error.message || 'Max retries exceeded'
          )

          enqueueSnackbar(
            `Operacja ${mutation.mutationType} nie powiodła się. Sprawdź kolejkę i spróbuj ponownie.`,
            { variant: 'error' }
          )
        }
      }
    }

    // Clean up completed mutations older than 24 hours
    setTimeout(() => {
      mutationQueue.clearCompleted()
    }, 1000)
  }

  return { processQueue }
}

// Helper function to invalidate cache based on mutation type
async function invalidateCacheForMutation(
  mutation: OfflineMutation,
  queryClient: ReturnType<typeof useQueryClient>
) {
  const { mutationType, protocolId } = mutation

  switch (mutationType) {
    case 'protocol':
      // Invalidate protocol lists - both acceptance and delivery protocols
      await queryClient.invalidateQueries({ queryKey: ['acceptanceProtocolList'] })
      await queryClient.invalidateQueries({ queryKey: ['deliveryProtocolList'] })

      // Also invalidate specific protocol if we have protocolId
      if (protocolId) {
        await queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] })
        await queryClient.invalidateQueries({
          queryKey: ['acceptanceProtocolListData', protocolId]
        })
        await queryClient.invalidateQueries({ queryKey: ['deliveryProtocolData', protocolId] })
        await queryClient.invalidateQueries({ queryKey: ['controlQuestionAnswer', protocolId] })
      }
      break

    case 'furniture':
      if (protocolId) {
        await queryClient.invalidateQueries({ queryKey: ['protocolFurniture', protocolId] })
        // Also invalidate protocol to refresh counts/status
        await queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] })
      }
      break

    case 'item':
      if (protocolId) {
        await queryClient.invalidateQueries({ queryKey: ['protocolItem', protocolId] })
        await queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] })
      }
      break

    case 'key':
      if (protocolId) {
        await queryClient.invalidateQueries({ queryKey: ['protocolKey', protocolId] })
        await queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] })
      }
      break

    case 'animal':
      if (protocolId) {
        await queryClient.invalidateQueries({ queryKey: ['protocolAnimal', protocolId] })
        await queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] })
      }
      break

    case 'meter':
      if (protocolId) {
        await queryClient.invalidateQueries({ queryKey: ['protocolMeter', protocolId] })
        await queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] })
      }
      break

    case 'room':
      if (protocolId) {
        await queryClient.invalidateQueries({ queryKey: ['protocolRoom', protocolId] })
        await queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] })
      }
      break

    case 'sign':
      if (protocolId) {
        await queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] })
        // Invalidate protocol lists as signing changes status
        await queryClient.invalidateQueries({ queryKey: ['acceptanceProtocolList'] })
        await queryClient.invalidateQueries({ queryKey: ['deliveryProtocolList'] })
      }
      break

    default:
      console.warn(
        `Unknown mutation type: ${mutationType}, invalidating all protocol-related cache`
      )
      await queryClient.invalidateQueries({ queryKey: ['acceptanceProtocolList'] })
      await queryClient.invalidateQueries({ queryKey: ['deliveryProtocolList'] })
      if (protocolId) {
        await queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] })
      }
  }

  console.log(`Invalidated cache for mutation type: ${mutationType}, protocolId: ${protocolId}`)
}
